import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import _ from "lodash";
import moment from "moment";
import { Grid, FormControlLabel, Radio, CircularProgress } from "@material-ui/core";
import { ButtonSizeOptions, InputTypeAhead, SecondaryButton, LabelText } from "@loanbossinc/component-library";

import { actionCreators as drawerActionCreators } from "../lenderReportingDrawer.store";
import { actionCreators as fieldManagementActionCreators } from "../field-management/fieldManagement.store";
import HousesIcon from "../../houses.svg";
import SimpleInfoDialog from "components/Global/ConfirmationDialogs/SimpleInfoDialog";
import { actionCreators as sfManagementActionCreators } from "./sourceFileManagement.store";
import {
  BoldConfirmationTextWrapper,
  ConfirmationTextWrapper,
  ConfirmationWrapper,
  ControlRow,
  ControlRowItem,
  DescriptionWrapper,
  HousesIconWrapper,
  InputWrapper,
  LeftSectionWrapper,
  RightSectionWrapper,
  SectionWrapper,
  StyledPrimaryButton,
  InfoWrapper,
  RadioGroupWrapper,
  StyledRadioGroup,
  StyledSourceFileLoader
} from "./styles";
import SourceFileListItem from "./sourceFileListItem";
import SourceFileVersions from "./sourceFileVersions";

const SUPPORTED_FILE_TYPES = [".csv", ".xls", ".xlsx"];
const RADIO_BUTTONS_OPTIONS = [
  { value: "property", label: "Property" }
];

export const SourceFileManagement = props => {
  const {
    clearSourceFileManagementSelections,
    currentUserId,
    deal,
    dealSourceFiles,
    allVersionsForDeal,
    getSourceFilesByDeal,
    getSourceFileTypes,
    getSourceFileVersions,
    getAllVersions,
    isLoading,
    isOpen,
    isUploading,
    typeId,
    selectedDealId,
    selectedFile,
    selectedPropertyId,
    selectedSourceFile,
    isPortfolioLevel,
    setDrawerSelections,
    setSourceFileManagementSelections,
    selectedSourceFileVersions,
    sourceFileTypes,
    sourceId,
    uploadErrorMessage,
    uploadSourceFile,
    uploadSuccessful,
    setFieldSelections,
    setSourceFilesToDelete,
    largeMriFileModal,
    user: { id: userId }
  } = props;

  const companyId = props.user.contact?.companyId;

  useEffect(() => {
    if (isOpen) {
      // clear field selections in case user navigates back to field management
      setFieldSelections({
        fields: null,
        isAdding: false,
        isEditing: false,
        currentSelectedFieldType: null,
        isFromAddNewLineItem: false,
        isFromDebtYieldCalculation: false,
        isSelectingField: false,
        selectedField: null,
        noiValuesPerReportGroup: null
      });
    }
    if (isOpen && !dealSourceFiles) {
      setSourceFileManagementSelections({ currentUserId: userId, selectedDealId: deal.id });
      getSourceFilesByDeal({ dealId: deal.id });
      getAllVersions({ dealId: deal.id });
      getSourceFileTypes();
    }
    if (!isOpen) {
      clearSourceFileManagementSelections();
      setDrawerSelections({ dealSourceFiles: null });
      setSourceFileManagementSelections({ selectedSourceFile: null });
      setSourceFilesToDelete(null);
    }
  }, [deal, dealSourceFiles, isOpen]);

  useEffect(() => {
    if (uploadSuccessful) {
      clearSourceFileManagementSelections();
      setSourceFileManagementSelections({ currentUserId: userId, selectedDealId: deal.id });
      getSourceFilesByDeal({ dealId: deal.id });
      getAllVersions({ dealId: deal.id });
    }
  }, [uploadSuccessful, deal, userId]);

  useEffect(() => {
    if (selectedSourceFile) {
      getSourceFileVersions({ dealId: deal.id, selectedSourceFile });
    }
  }, [selectedSourceFile]);

  const hasOneProperty = Boolean(deal) && Boolean(deal.properties) && deal.properties.length === 1;
  const hasMultipleProperties = Boolean(deal) && Boolean(deal.properties) && deal.properties.length > 1;
  const properties = [];

  useEffect(() => {
    if (hasOneProperty && !selectedPropertyId) {
      setSourceFileManagementSelections({ selectedPropertyId: deal.properties[0].id });
    }
  }, [hasOneProperty, deal, selectedPropertyId]);

  if (hasMultipleProperties) {
    _.sortBy(deal.properties, ["name"]).forEach(x => properties.push({ id: x.id, name: x.name }));
  }

  const onCancelSourceFileSelection = () => {
    setSourceFileManagementSelections({ selectedFile: null });
  };

  let sourceFileTypesForSelectedSource = [];
  const isPropertySelected = hasOneProperty || (hasMultipleProperties && selectedPropertyId);
  if (sourceId && sourceFileTypes && isPropertySelected) {
    const { types } = sourceFileTypes.find((x) => x.id === sourceId);

    const filteredTypes = types.filter(
      (t) =>
        !dealSourceFiles ||
        !dealSourceFiles.find((dsf) => {
          const isPropertyMatchedForMulti = Boolean(selectedPropertyId) && dsf.propertyId === selectedPropertyId;

          return (hasOneProperty || isPropertyMatchedForMulti) && dsf.sourceId === sourceId && dsf.typeId === t.id;
        })
    );
    sourceFileTypesForSelectedSource = _.sortBy(filteredTypes, ["name"]);
  }

  const setSourceId = value => {
    if (value) {
      setSourceFileManagementSelections({ sourceId: value, typeId: null });
    }
  };

  const setTypeId = value => {
    if (value) {
      setSourceFileManagementSelections({ typeId: value });
    } else {
      setSourceFileManagementSelections({ typeId: null });
    }
  };

  const setSelectedPropertyId = value => {
    if (value) {
      setSourceFileManagementSelections({ selectedPropertyId: value });
    } else {
      setSourceFileManagementSelections({ selectedPropertyId: null });
    }
  };

  const getDealSourceFileList = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", width: "100%", marginTop: "1em" }}>
          <CircularProgress size={40} style={{ color: "#005AA9" }} />
        </div>
      );
    }

    if (!dealSourceFiles) {
      return <></>;
    }

    let sourceFilesToShow = dealSourceFiles;

    const rentRollFileTypes = ["rent-roll", "mf-rent-roll", "mf-rent-roll-charge-code"];
    // since rent roll files don't follow the pattern of having parentId,
    // we need to leave just one the oldest file for rent roll type per property
    const rentRollSourceFiles = _.orderBy(dealSourceFiles.filter(
      (sf) => (rentRollFileTypes.includes(sf.typeId))), ["createdAt"], ["asc"]);
    if (rentRollSourceFiles.length) {
      const rentRollsGroupedByProperty = _.groupBy(rentRollSourceFiles, "propertyId");
      const rentRollsIdsToShow = [];
      _.forOwn(rentRollsGroupedByProperty, (rrFiles) => {
        const rentRollsGroupedBySource = _.groupBy(rrFiles, "sourceName");
        _.forOwn(rentRollsGroupedBySource, (file) => {
          rentRollsIdsToShow.push(file[0].id);
        });
      });
      sourceFilesToShow = sourceFilesToShow.filter(sf => {
        const isRentRollType = rentRollFileTypes.includes(sf.typeId);
        return !isRentRollType || rentRollsIdsToShow.includes(sf.id);
      });
    }

    return _.sortBy(sourceFilesToShow, ["sourceName", "typeName"]).map(dealSourceFile => {
      let summary = "Property";
      if (dealSourceFile.propertyId) {
        const dealProperty = Boolean(deal) && deal.properties.find(x => x.id.toString() === dealSourceFile.propertyId.toString());
        if (dealProperty) {
          summary = dealProperty.name;
        }
      }

      let associatedFiles = [];

      if (rentRollFileTypes.includes(dealSourceFile.typeId)) {
        associatedFiles = allVersionsForDeal && allVersionsForDeal
        .filter((v) => (rentRollFileTypes.includes(v.typeId))
          && v.propertyId === dealSourceFile.propertyId
          && v.sourceName === dealSourceFile.sourceName);
      } else {
        associatedFiles = allVersionsForDeal && allVersionsForDeal.filter(file => file.id === dealSourceFile.id || file.parentId === dealSourceFile.id);
      }

      const orderOptionsFns = [associatedFile => moment(associatedFile.asOfDate), associatedFile => moment(associatedFile.updatedAt)];
      const sortedAssociatedFiles = _.orderBy(associatedFiles, orderOptionsFns, ["desc", "desc"]);
      const latestAsOfDate = sortedAssociatedFiles.length ? sortedAssociatedFiles[0].asOfDate : null;
      const latestCreatedAtDate = sortedAssociatedFiles.length ? sortedAssociatedFiles[0].createdAt : null;

      return (
        <SourceFileListItem
          key={dealSourceFile.id}
          summary={summary}
          sourceFileId={dealSourceFile.id}
          sourceName={dealSourceFile.sourceName}
          typeName={dealSourceFile.typeName}
          createdAt={dealSourceFile.createdAt}
          latestAsOfDate={latestAsOfDate}
          latestCreatedAtDate={latestCreatedAtDate}
          onClick={() => setSourceFileManagementSelections({ selectedSourceFile: dealSourceFile })}
        />
      );
    });
  };

  const onConfirmUpload = event => {
    event.persist();
    const debounceDelay = 250;
    const handleChangeDebounce = _.debounce(() => {
      if (!isUploading) {
        uploadSourceFile(selectedDealId, selectedPropertyId, currentUserId, sourceId, typeId, selectedFile, isPortfolioLevel, companyId);
      }
    }, debounceDelay);

    handleChangeDebounce(event);
  };

  const backToList = () => {
    setSourceFileManagementSelections({ selectedSourceFile: null, selectedSourceFileVersions: null });
    setSourceFilesToDelete(null);
  };

  const handleUploadError = errors => {
    const error = errors[0].errors[0];

    let message;
    switch (error.code) {
      case "file-too-large":
        message = "File size has to be 1 Mb or less";
        break;
      case "file-invalid-type":
        message = "File type must be one of csv, xls, xlsx";
        break;
      default:
        message = "Unsupported File";
    }

    setSourceFileManagementSelections({ uploadErrorMessage: message });
  };

  const isAttachDisabled = !sourceId || !typeId || (!selectedPropertyId && hasMultipleProperties);

  if (selectedSourceFile) {
    return (
      <SourceFileVersions
        backToList={backToList}
        dealId={deal.id}
        getSourceFileVersions={getSourceFileVersions}
        getSourceFilesByDeal={getSourceFilesByDeal}
        selectedSourceFile={selectedSourceFile}
        selectedSourceFileVersions={selectedSourceFileVersions}
        isOpen={isOpen}
        companyId={companyId}
        setSourceFilesToDelete={setSourceFilesToDelete}
        getAllVersions={getAllVersions}
      />
    );
  }

  const loaderMessage = !uploadErrorMessage ? "Add a file by dragging it here, or" : uploadErrorMessage;
  const fileLoaderTextContent = !isUploading ? loaderMessage : "Uploading ...";

  return (
    <>
      {hasMultipleProperties && (
        <>
          <InfoWrapper>
            <Grid container justify="flex-start" alignItems="center" direction="row" style={{ padding: "0 0.5em" }}>
              <Grid item xs={1}>
                <HousesIconWrapper>
                  <img src={HousesIcon} alt="Houses Icon" height={40} width={32} />
                </HousesIconWrapper>
              </Grid>
              <Grid item xs={11}>
                <DescriptionWrapper>This deal has multiple properties. Show files for the portfolio or a specific property.</DescriptionWrapper>
              </Grid>
            </Grid>
          </InfoWrapper>
          <SectionWrapper>
            <LeftSectionWrapper>
              <RadioGroupWrapper>
                <StyledRadioGroup
                  value={isPortfolioLevel ? "portfolio" : "property"}
                  aria-label="portfolio-or-property"
                  name="sourceFileLevel"
                  onChange={e => {
                    setSourceFileManagementSelections({ isPortfolioLevel: e.target.value === "portfolio" });
                  }}
                >
                  {RADIO_BUTTONS_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={<LabelText value={option.label} style={{ fontSize: "16px" }} />}
                    />
                  ))}
                </StyledRadioGroup>
              </RadioGroupWrapper>
            </LeftSectionWrapper>
            <RightSectionWrapper style={{ height: "initial" }}>
              <InputWrapper>
                <ControlRow>
                  <ControlRowItem>
                    <InputTypeAhead
                      variant="filled"
                      id="selectedPropertyId"
                      name="Property"
                      label="Property"
                      options={properties}
                      displayWith={option => option && (option.name || "unknown")}
                      setValueWith={option => option && option.id}
                      onChange={setSelectedPropertyId}
                      value={selectedPropertyId}
                    />
                  </ControlRowItem>
                </ControlRow>
              </InputWrapper>
            </RightSectionWrapper>
          </SectionWrapper>
        </>
      )}
      <SectionWrapper>
        <LeftSectionWrapper style={{ height: "136px" }}>
          {(!selectedFile || Boolean(uploadErrorMessage)) && (
            <StyledSourceFileLoader
              content={fileLoaderTextContent}
              multiple={false}
              onDrop={(files, error) => {
                if (error.length) {
                  handleUploadError(error);
                }
                if (files.length) {
                  setSourceFileManagementSelections({ selectedFile: files[0], uploadSuccessful: false, uploadErrorMessage: null });
                }
              }}
              accept={SUPPORTED_FILE_TYPES}
              maxSize={100000000}
            />
          )}
          {Boolean(selectedFile) && !uploadErrorMessage && (
            <ConfirmationWrapper>
              <BoldConfirmationTextWrapper>{selectedFile.name}</BoldConfirmationTextWrapper>
              <ConfirmationTextWrapper>will be attached to the selected property</ConfirmationTextWrapper>
              <div>
                <SecondaryButton size="sm" id="cancel-source-file-selection" onClick={onCancelSourceFileSelection} label="Cancel" />
                <StyledPrimaryButton size={ButtonSizeOptions.sm} label="Attach" onClick={onConfirmUpload} disabled={isAttachDisabled} />
              </div>
            </ConfirmationWrapper>
          )}
        </LeftSectionWrapper>
        <RightSectionWrapper>
          <InputWrapper>
            <ControlRow>
              <ControlRowItem>
                <InputTypeAhead
                  variant="filled"
                  id="sourceId"
                  name="Source"
                  label="Source"
                  disabled={hasMultipleProperties && !selectedPropertyId}
                  options={sourceFileTypes}
                  displayWith={option => option && option.source_name}
                  setValueWith={option => option && option.id}
                  onChange={setSourceId}
                  value={sourceId}
                />
              </ControlRowItem>
            </ControlRow>
          </InputWrapper>
          <InputWrapper>
            <ControlRow>
              <ControlRowItem>
                <InputTypeAhead
                  variant="filled"
                  id="typeId"
                  name="Type"
                  label="Type"
                  disabled={!sourceId || (hasMultipleProperties && !selectedPropertyId)}
                  options={sourceFileTypesForSelectedSource}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  onChange={setTypeId}
                  value={typeId}
                />
              </ControlRowItem>
            </ControlRow>
          </InputWrapper>
        </RightSectionWrapper>
      </SectionWrapper>
      {!isLoading && dealSourceFiles && Boolean(dealSourceFiles.length) && getDealSourceFileList()}
      <SimpleInfoDialog
        title="Loading MRI file"
        message="File upload is processing. This may take several minutes."
        modalOpen={largeMriFileModal}
        handleClose={() => setSourceFileManagementSelections({ largeMriFileModal: false })}
        handleAccept={() => setSourceFileManagementSelections({ largeMriFileModal: false })}
      />
    </>

  );
};

const mapStateToProps = ({ lenderReportingDrawer, lenderReportSourceFileManagement, authentication: { user } }) => ({
  user,
  ...lenderReportingDrawer,
  ...lenderReportSourceFileManagement
});
const mapDispatchToProps = dispatch => bindActionCreators({ ...drawerActionCreators, ...sfManagementActionCreators, ...fieldManagementActionCreators }, dispatch);

export default compose(connect(mapStateToProps, mapDispatchToProps))(SourceFileManagement);
